import parse from 'date-fns/parse';
import getHours from 'date-fns/getHours';
import getMinutes from 'date-fns/getMinutes';
import timeZoneData from './timezones.json';

export const defaultTimeSlot = [
  [{ day: 0, to: '', from: '', valid: false }],
  [{ day: 1, to: '', from: '', valid: false }],
  [{ day: 2, to: '', from: '', valid: false }],
  [{ day: 3, to: '', from: '', valid: false }],
  [{ day: 4, to: '', from: '', valid: false }],
  [{ day: 5, to: '', from: '', valid: false }],
  [{ day: 6, to: '', from: '', valid: false }],
];

export const generateTimeSlots = (step = 15) => {
  /* 
    Generates a list of time strings from 12:00 AM to next 24 hours. Each new string
     will be generated by adding `step` minutes to the previous one.
    The list is generated by starting with a random day and adding step minutes till end of the same day.
    Always includes 11:59 PM as the final slot to complete the day.
  */
  const date = new Date(1970, 1, 1);
  const slots = [];
  while (date.getDate() === 1) {
    slots.push(
      date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    );
    date.setMinutes(date.getMinutes() + step);
  }

  // Always add 11:59 PM as the final slot if it's not already included
  const lastSlot = '11:59 PM';
  if (!slots.includes(lastSlot)) {
    slots.push(lastSlot);
  }

  return slots;
};

export const getTime = (hour, minute) => {
  const merdian = hour > 11 ? 'PM' : 'AM';
  const modHour = hour > 12 ? hour % 12 : hour || 12;
  const parsedHour = modHour < 10 ? `0${modHour}` : modHour;
  const parsedMinute = minute < 10 ? `0${minute}` : minute;
  return `${parsedHour}:${parsedMinute} ${merdian}`;
};

export const timeSlotParse = timeSlots => {
  // Parse slots
  const parsed = timeSlots.map(slot => {
    const {
      day_of_week: day,
      open_hour: openHour,
      open_minutes: openMinutes,
      close_hour: closeHour,
      close_minutes: closeMinutes,
      closed_all_day: closedAllDay,
      open_all_day: openAllDay,
    } = slot;
    const from = closedAllDay ? '' : getTime(openHour, openMinutes);
    const to = closedAllDay ? '' : getTime(closeHour, closeMinutes);

    // For closed days that still have hour data stored (from a previous save),
    // preserve it as lastFrom/lastTo so we can restore it when the day is re-enabled.
    const hasStoredHours =
      typeof openHour === 'number' && typeof closeHour === 'number';
    const lastFrom =
      closedAllDay && hasStoredHours
        ? getTime(openHour, openMinutes ?? 0)
        : null;
    const lastTo =
      closedAllDay && hasStoredHours
        ? getTime(closeHour, closeMinutes ?? 0)
        : null;

    return {
      day,
      to,
      from,
      valid: !closedAllDay,
      openAllDay,
      ...(lastFrom && lastTo ? { lastFrom, lastTo } : {}),
    };
  });

  // Group by day_of_week into an array of arrays (7 elements, one for each day)
  // Ensure we have all 7 days (0-6), filling missing days with defaults
  const result = [[], [], [], [], [], [], []];

  parsed.forEach(slot => {
    result[slot.day].push(slot);
  });

  // Fill empty days with default structure
  result.forEach((daySlots, index) => {
    if (daySlots.length === 0) {
      result[index] = [{ day: index, to: '', from: '', valid: false }];
    }
  });

  return result;
};

export const timeSlotTransform = timeSlots => {
  // Flatten the array of arrays
  const flatSlots = timeSlots.reduce((acc, curr) => [...acc, ...curr], []);

  return flatSlots.map(slot => {
    const closed = slot.openAllDay ? false : !(slot.to && slot.from);
    const openAllDay = slot.openAllDay;
    let fromDate = '';
    let toDate = '';
    let openHour = '';
    let openMinutes = '';
    let closeHour = '';
    let closeMinutes = '';

    if (!closed) {
      fromDate = parse(slot.from, 'hh:mm a', new Date());
      toDate = parse(slot.to, 'hh:mm a', new Date());
      openHour = getHours(fromDate);
      openMinutes = getMinutes(fromDate);
      closeHour = getHours(toDate);
      closeMinutes = getMinutes(toDate);
    } else if (slot.lastFrom && slot.lastTo) {
      // Day is closed but has previously saved hours — persist them in the backend
      // so they can be restored when the day is re-enabled (even after a page reload).
      fromDate = parse(slot.lastFrom, 'hh:mm a', new Date());
      toDate = parse(slot.lastTo, 'hh:mm a', new Date());
      openHour = getHours(fromDate);
      openMinutes = getMinutes(fromDate);
      closeHour = getHours(toDate);
      closeMinutes = getMinutes(toDate);
    }

    return {
      day_of_week: slot.day,
      closed_all_day: closed,
      open_hour: openHour,
      open_minutes: openMinutes,
      close_hour: closeHour,
      close_minutes: closeMinutes,
      open_all_day: openAllDay,
    };
  });
};

export const timeZoneOptions = () => {
  return Object.keys(timeZoneData).map(key => ({
    label: key,
    value: timeZoneData[key],
  }));
};
